import {Injectable} from '@angular/core';
import {UntypedFormGroup} from "@angular/forms";
import {BehaviorSubject} from "rxjs";
import {AddressService, DiagnosisService} from "../../../../shared/services";
import * as Sentry from '@sentry/angular-ivy'
import {PhysicianService} from "../../../../shared/services/physician.service";
import {BillingInformation, ResponsibleParty, ZipCode} from "../../../../shared/models";
import {BillingService} from "../../../../shared/services/billing.service";

const missingInfoJson = {
  "Patient Birth Date": "",
  "Gender": "",
  "Ordering Physician": {
    "Last Name": "",
    "First Name": "",
    "NPI": ""
  },
  "Diagnoses ICD-10 Codes": [{
    "code": "",
    "name": ""
  }],
  "Insurance Information": [{
    "Payer or Insurance Company or Name": "",
    "Insurance Id or Policy or Member Id": "",
    "Group Number": "",
    "Insured Last Name": "",
    "Insured First Name": "",
    "Insured Middle Name": "",
    "Insured Birth Date": "",
    "Insured Gender or Sex": "",
    "Insured Relationship": "",
    "Insured Address": "",
    "Insured City": "",
    "Insured State": "",
    "Insured ZipCode": "",
    "Insured Phone Number": ""
  }],
  "Responsible Parties or Contacts": [{
    "Last Name": "",
    "First Name": "",
    "Phone Number": "",
    "Mobile": "",
    "Fax": "",
    "Address": "",
    "City": "",
    "State": "",
    "ZipCode": ""
  }],
  "Comments": ""
};

const questions = [
  `Fill the following json [jsonString] extracting the information from the following text, it's important to return always the provided json even if you can't perform the action (provide the json only, no text added): `,
  `Fill the ICD-10 codes [jsonString] include all possible ICD-10 codes strictly from existing symptoms and diagnoses in the following text, it's important to return always the provided json even if you can't perform the action (provide the json only, no text added): `
];

@Injectable({
  providedIn: 'root'
})
export class MissingInfoAutocompleteService {
  private missingInfoForm?: UntypedFormGroup;
  private itemAutocompleted: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
  private isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private diagnosisService: DiagnosisService, private physicianService: PhysicianService,
              private addressService: AddressService, private billingService: BillingService) {
  }

  get MissingInfoJson(): any {
    return missingInfoJson;
  }

  get Questions(): string[] {
    return questions;
  }

  get IsLoading(): BehaviorSubject<boolean> {
    return this.isLoading$;
  }

  get Autocompleted(): BehaviorSubject<void> {
    return this.itemAutocompleted;
  }

  set MissingInfoForm(value: UntypedFormGroup) {
    this.missingInfoForm = value;
  }

  async onJsonChange(json: any, patientId: number): Promise<void> {
    try {
      this.isLoading$.next(true);
      if (Object.keys(json).length == 0) return;
      let hasChanges = false;

      if (json["Diagnoses ICD-10 Codes"]) {
        const diagnoses = (json["Diagnoses ICD-10 Codes"] as []).map((x: { code: string, name: string }) => x.code);
        if (diagnoses.length > 0 && await this.handleDiagnoses(diagnoses)) {
          hasChanges = true;
        }
      }

      if ((json["Insurance Information"] as []).length > 0) {
        if (await this.handleInsuranceInformation(json["Insurance Information"], patientId))
          hasChanges = true;
      }

      if ((json["Responsible Parties or Contacts"] as []).length > 0) {
        if (await this.handleResponsibleParties(json["Responsible Parties or Contacts"], patientId))
          hasChanges = true;
      }

      if (json["Comments"] && json["Comments"] !== "") {
        if (this.handleComments(json))
          hasChanges = true;
      }

      if (json["Patient Birth Date"] && json["Patient Birth Date"] !== "") {
        if (this.HandleBirthday(json, hasChanges))
          hasChanges = true;
      }

      if (json["Gender"] && json["Gender"] !== "") {
        if (this.handleGender(json, hasChanges))
          hasChanges = true;
      }

      if (json["Ordering Physician"]) {
        if (await this.handlePhysician(json, hasChanges))
          hasChanges = true;
      }

      if (hasChanges) {
        this.itemAutocompleted.next();
      }
    } catch (e) {
      Sentry.captureException(e)
    } finally {
      this.isLoading$.next(false);
    }
  }

  private async handleInsuranceInformation(insuranceInfo: [], patientId: number): Promise<boolean> {
    const insuranceInformation: BillingInformation[] = [];

    for (const info of insuranceInfo) {

      try {
        const item = new BillingInformation();
        item.patientId = patientId;

        const gender = (info["Insured Gender or Sex"] as string).toUpperCase();
        if (gender == "MALE" || gender == "FEMALE") {
          item.gender = gender;
        }

        item.firstName = info["Insured First Name"];
        item.lastName = info["Insured Last Name"];
        item.middleName = info["Insured Middle Name"];

        try {
          const date = new Date(info["Insured Birth Date"]);
          if (!isNaN(date.getTime()))
            item.birthDate = date;
        } catch (e) {
          console.error("Invalid date format to assign to Insured Birth Date in auto-complete");
        }

        const relationship = info["Insured Relationship"];

        switch ((relationship as string ?? '').toLowerCase()) {
          case 'self':
            item.relationship = "SELF";
            break;
          case 'spouse':
            item.relationship = "SPOUSE";
            break;
          case 'father':
            item.relationship = "FATHER";
            break;
          case 'mother':
            item.relationship = "MOTHER";
            break;
          case 'son':
            item.relationship = "SON";
            break;
          case 'daughter':
            item.relationship = "DAUGHTER";
            break;
        }

        item.address = info["Insured Address"];
        item.phone = info["Insured Phone Number"];
        item.groupNumber = info["Group Number"];
        item.insuranceId = info["Insurance Id or Policy or Member Id"];
        item.differentThanPatient = (item.relationship as string ?? '').toLowerCase() !== 'self';

        let zipCode: ZipCode | null = null;

        if (info["Insured ZipCode"] !== '') {
          zipCode = await this.addressService.getZipCodeByCode(info["Insured ZipCode"]).toPromise()
        }

        if (zipCode == null) {
          item.address = `${item.address} ${info["Insured City"]} ${info["Insured State"]} ${info["Insured ZipCode"]}`;
        } else {
          item.zipCode = zipCode;
          item.zipCodeId = zipCode.id;
          item.zipCodeName = zipCode.code;
        }

        const payer = await this.billingService.getBestMatch((info["Payer or Insurance Company or Name"] as string).toUpperCase()).toPromise();
        item.payer = payer.name;
        item.payerId = payer.id;
        item.billTypeId = payer.billType?.id;
        item.billType = payer.billType?.code;

        if (item.payerId == null || item.billTypeId == null) continue;

        insuranceInformation.push(item);
      } catch (e) {
        Sentry.captureException(e);
      }
    }

    if (insuranceInformation.length > 0) {
      const existingInsuranceInfo = this.missingInfoForm?.get('insuranceInformation')?.value ?? [];
      const maxSort = (existingInsuranceInfo as BillingInformation[] ?? []).reduce((prev, current) => {
        return ((prev as any).order ?? 0) > ((current as any).order ?? 0) ? prev : current;
      });

      for (const information of insuranceInformation) {
        (information as any).order = (maxSort as any).order + 1;
      }

      this.missingInfoForm?.get('insuranceInformation')?.setValue([...existingInsuranceInfo, ...insuranceInformation]);
      return true;
    } else {
      return false;
    }
  }

  private handleComments(json: any) {
    //todo do not override the comment
    const comment = `<html lang="eng"><head><title></title></head><body>${json["Comments"]}</body></html>`
    this.missingInfoForm?.get('comment')?.setValue(comment);
    return true;
  }

  private HandleBirthday(json: any, hasChanges: boolean) {
    if (this.missingInfoForm?.get("birthDate")?.value == null) {
      try {
        const date = new Date(json["Patient Birth Date"]);
        if (!isNaN(date.getTime()))
          this.missingInfoForm?.get('birthDate')?.setValue(date);
        hasChanges = true;
      } catch (e) {
        console.error("Invalid date format to assign to Patient Birth Date in auto-complete");
      }
    }
    return hasChanges;
  }

  private handleGender(json: any, hasChanges: boolean) {
    if (this.missingInfoForm?.get("gender")?.value == null) {
      const gender = json["Gender"].toUpperCase();
      if (gender == "MALE" || gender == "FEMALE") {
        this.missingInfoForm?.get("gender")?.setValue(gender);
        hasChanges = true;
      }
    }
    return hasChanges;
  }

  private async handlePhysician(json: any, hasChanges: boolean) {
    if (this.missingInfoForm?.get('orderingPhysician')?.value == null) {
      const physician = json["Ordering Physician"];

      if (physician["NPI"] && physician["NPI"] !== "") {
        hasChanges = await this.handlePhysicianByNpi(physician["NPI"]);
      } else if (physician["Last Name"] && physician["Last Name"] !== "" && physician["First Name"] && physician["First Name"] !== "") {
        hasChanges = await this.handlePhysicianByName(physician["Last Name"], physician["First Name"]);
      }
    }
    return hasChanges;
  }

  private async handleResponsibleParties(responsibleParties: [], patientId: number): Promise<boolean> {
    const rpList = [];
    for (const responsibleParty of responsibleParties) {
      if (responsibleParty["Last Name"] === '' || responsibleParty["First Name"] === '') continue;

      try {
        const rp = new ResponsibleParty();
        rp.lastName = responsibleParty["Last Name"];
        rp.firstName = responsibleParty["First Name"];
        rp.phone = responsibleParty["Phone Number"];
        rp.mobile = responsibleParty["Mobile"];
        rp.fax = responsibleParty["Fax"];
        rp.address = responsibleParty["Address"];
        rp.patientId = patientId;

        let zipCode: ZipCode | null = null;

        if (responsibleParty["ZipCode"] !== '') {
          zipCode = await this.addressService.getZipCodeByCode(responsibleParty["ZipCode"]).toPromise()
        }

        if (zipCode == null) {
          rp.address = `${rp.address} ${responsibleParty["City"]} ${responsibleParty["State"]} ${responsibleParty["ZipCode"]}`;
        } else {
          rp.zipCode = zipCode;
          rp.zipCodeId = zipCode.id;
          rp.zipCodeName = zipCode.code;
        }
        rpList.push(rp);
      } catch (e) {
        Sentry.captureException(e);
      }
    }

    if (rpList.length > 0) {
      const existingResponsibleParties = this.missingInfoForm?.get('responsibleParties')?.value ?? [];
      this.missingInfoForm?.get('responsibleParties')?.setValue([...existingResponsibleParties, ...rpList]);
      return true;
    } else {
      return false;
    }
  }

  private async handleDiagnoses(diagnoses: string[]): Promise<boolean> {
    try {
      const match = await this.diagnosisService.getBestMatch(diagnoses).toPromise();
      const existingDiagnoses = this.missingInfoForm?.get('diagnoses')?.value ?? [];
      const newDiagnoses = match.filter(x => !existingDiagnoses.includes(x.id));

      if (newDiagnoses.length > 0) {
        this.missingInfoForm?.get('diagnoses')?.setValue([...existingDiagnoses, ...newDiagnoses.map(x => x.id)]);
        return true;
      }

      return false;
    } catch (e) {
      Sentry.captureException(e);
      return false;
    }
  }

  private async handlePhysicianByNpi(npi: string): Promise<boolean> {
    try {
      const match = await this.physicianService.getBestMatchByNpi(npi).toPromise();
      this.missingInfoForm?.get('orderingPhysician')?.setValue(match);
      return true;
    } catch (e) {
      Sentry.captureException(e);
      return false;
    }
  }

  private async handlePhysicianByName(lastName: string, firstName: string): Promise<boolean> {
    try {
      const match = await this.physicianService.getBestMatchByName(firstName, lastName).toPromise();
      this.missingInfoForm?.get('orderingPhysician')?.setValue(match);
      return true;
    } catch (e) {
      Sentry.captureException(e);
      return false;
    }
  }
}
